import { getSerialized } from '@extension/wa-communication-layer/core/utils/wa-key-serialization';
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Typography, Paper, Divider, IconButton, Box } from "@mui/material";
import { Close } from "@mui/icons-material";

import ChatMessages from "@common-components/chat-messages/chat-messages";
import { useAppSelector, useAppDispatch } from "@common-reducers/hooks/store.hook";
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import {
  selectMessageCountsByStatus,
  selectFilteredMessagesForCurrentChat,
  selectClearableRecurringCounts,
} from '@/common/reducers/UserSelectors';
import { waWallpaperSx } from '@common/components/wa-wallpaper/wa-wallpaper';
import { btLayoutSidebarActions } from "@common-reducers/BtLayoutSidebarReducer";
import RowStack from "@/common/components/common/row-stack/RowStack";
import { getWaContactDisplayLabel } from '@/common/reducers/WhatsAppSelectors';
import NewMessageFab from '@/common/components/new-message-fab/new-message-fab';
import MessageStatusHeader from '@/common/components/scheduled-messages/message-status-header';
import { MessageClearConfirmationDialog } from '@/common/components/scheduled-messages/message-clear-confirmation-dialog';
import {
  deleteAllMessagesForCurrentChatThunk,
  deleteAllMessagesByStatusThunk,
  refreshChatScheduledMessagesThunk
} from '@common/thunks/UserMessagesThunks';

type ChatScheduledMessagesProps = {
  hideHeader?: boolean;
}

const ChatScheduledMessages = React.memo((props: ChatScheduledMessagesProps) => {
  const { t } = useTranslation(namespace);
  const currentChat = useAppSelector((state) => state.WhatsAppReducer.currentChat);
  const dispatch = useAppDispatch();

  // Visibility filters state
  const [visibleStatuses, setVisibleStatuses] = useState({
    pending: true,
    failed: true
  });

  // Confirmation dialog state
  const [confirmationDialog, setConfirmationDialog] = useState<{
    open: boolean;
    status: 'pending' | 'failed' | 'all';
    count: number;
    recurringCount: number;
  }>({
    open: false,
    status: 'pending',
    count: 0,
    recurringCount: 0
  });
  const recurringCounts = useAppSelector(selectClearableRecurringCounts);

  // Re-sync with the server whenever this panel opens on a chat. A sent message is
  // removed from the queue server-side, but that patch does not always reach an open
  // client live — without this the panel keeps showing it as "Sent - not confirmed"
  // (with a red "missed" due time) until a full page refresh.
  const currentChatId = getSerialized(currentChat?.id);
  useEffect(() => {
    if (!currentChatId) return;
    dispatch(refreshChatScheduledMessagesThunk());
  }, [dispatch, currentChatId]);

  // Get message counts
  const messageCounts = useAppSelector(selectMessageCountsByStatus);

  // Get filtered messages using selector with visibleStatuses parameter
  const filteredMessages = useAppSelector((state) =>
    selectFilteredMessagesForCurrentChat(state, visibleStatuses)
  );

  // Memoize close handler
  const handleClose = useCallback(() => {
    dispatch(btLayoutSidebarActions.closeSidebar());
  }, [dispatch]);

  // Handle toggle status visibility
  const handleToggleStatus = useCallback((status: 'pending' | 'failed') => {
    setVisibleStatuses((prev) => ({
      ...prev,
      [status]: !prev[status]
    }));
  }, []);

  // Handle clear status
  const handleClearStatus = useCallback((status: 'pending' | 'failed') => {
    const count = status === 'pending' ? messageCounts.pending :
        messageCounts.failed;
    setConfirmationDialog({
      open: true,
      status,
      count,
      recurringCount: recurringCounts[status]
    });
  }, [messageCounts, recurringCounts]);

  // Handle clear all
  const handleClearAll = useCallback(() => {
    setConfirmationDialog({
      open: true,
      status: 'all',
      // #166: only pending/failed are actually cleared, so the count must not
      // include `sending` (sent_pending_ack) or the dialog overstates the delete.
      count: messageCounts.pending + messageCounts.failed,
      recurringCount: recurringCounts.all
    });
  }, [messageCounts.pending, messageCounts.failed, recurringCounts.all]);

  // Handle confirmation
  const runClear = useCallback(async (skipRecurring: boolean) => {
    if (confirmationDialog.status === 'all') {
      await dispatch(deleteAllMessagesForCurrentChatThunk({ skipRecurring }));
    } else {
      await dispatch(deleteAllMessagesByStatusThunk({ status: confirmationDialog.status, skipRecurring }));
    }
    setConfirmationDialog({ open: false, status: 'pending', count: 0, recurringCount: 0 });
  }, [confirmationDialog, dispatch]);
  const handleConfirmClear = useCallback(() => runClear(false), [runClear]);
  // #708 — the safe option: clear everything except the recurring series.
  const handleConfirmOneTimeOnly = useCallback(() => runClear(true), [runClear]);

  // Handle cancel
  const handleCancelClear = useCallback(() => {
    setConfirmationDialog({ open: false, status: 'pending', count: 0, recurringCount: 0 });
  }, []);

  // Memoize paper styles. The wallpaper is themed by CSS (see wa-wallpaper.ts):
  // it used to key on `whatsappDarkMode`, which the web app never writes.
  const paperStyles = useMemo(() => ({
    width: '100%',
    height: '100%',
    ...waWallpaperSx(),
    borderRadius: 2,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minWidth: '400px'
  }), []);

  return (
    <Paper elevation={0} sx={paperStyles}>
      {!props.hideHeader && (
        <Box sx={{ backgroundColor: 'var(--color-surface)' }}>
          <RowStack alignItems="center" sx={{ justifyContent: 'space-between', px: 2, py: 1 }}>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>

            <Typography
              variant="h6"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                py: '6px',
                color: 'var(--color-text)',
                flex: 1,
                textAlign: 'center',
                fontSize: '14px'
              }}
            >
              {currentChat ? (
                <>
                  <span style={{ fontSize: '13px', fontWeight: 400 }}>{t('scheduledMessages.forContact', 'Scheduled Messages for')}</span>
                  <br />
                  <span style={{ fontSize: '12px' }}>{getWaContactDisplayLabel(currentChat)}</span>
                </>
              ) : (
                t('scheduledMessages.noChatSelected', 'No chat selected.')
              )}
            </Typography>

            <Box sx={{ width: '40px' }} />
          </RowStack>
          <Divider />
        </Box>
      )}

      {
        messageCounts.total > 0 && (
          <MessageStatusHeader
            messageCounts={messageCounts}
            visibleStatuses={visibleStatuses}
            onToggleStatus={handleToggleStatus}
            onClearStatus={handleClearStatus}
            onClearAll={handleClearAll}
          />)
      }

      <Box sx={{
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        justifyContent: 'center',

        p: 2
      }}>
        <ChatMessages filteredMessages={filteredMessages} />
        <NewMessageFab />
      </Box>

      <MessageClearConfirmationDialog
        open={confirmationDialog.open}
        status={confirmationDialog.status}
        count={confirmationDialog.count}
        recurringCount={confirmationDialog.recurringCount}
        onConfirm={handleConfirmClear}
        onConfirmOneTimeOnly={handleConfirmOneTimeOnly}
        onCancel={handleCancelClear}
      />
    </Paper >
  );
});

export default ChatScheduledMessages;